import { useNavigate } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackSnackbar } from '../../../components/FeedbackSnackbar';
import { ListPagination } from '../../../components/ListPagination';
import { useOfferRequestsList } from '../api/useOfferRequestsList';
import { useUpdateOfferRequestStatus } from '../api/useUpdateOfferRequestStatus';
import { OfferRequestsFiltersPanel } from '../components/OfferRequestsFiltersPanel';
import { OfferRequestsPageLayout } from '../components/OfferRequestsPageLayout';
import { OpenOfferRequestsList } from '../components/OpenOfferRequestsList';
import { pageRange, useOfferRequestsView } from '../hooks/useOfferRequestsView';

const VIEW_SESSION_KEY = 'open-requests.view';

export function OpenOfferRequestsPage(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const view = useOfferRequestsView('open', VIEW_SESSION_KEY);
    const updateStatus = useUpdateOfferRequestStatus();

    const query = useOfferRequestsList(view.query);
    const total = query.data?.total ?? 0;
    const offerRequests = query.data?.items ?? [];

    return (
        <>
            <OfferRequestsPageLayout
                title={t('OFFER_REQUESTS.OPEN.TITLE')}
                filters={<OfferRequestsFiltersPanel value={view.filters} onChange={view.changeFilters} />}
                status={query.status}
                count={total}
                range={pageRange(view, total, offerRequests.length)}
                sort={view.sort}
                onSortChange={view.changeSort}
                footer={
                    total > 0 ? (
                        <ListPagination
                            page={view.page}
                            pageSize={view.pageSize}
                            total={total}
                            onPageChange={view.changePage}
                            onPageSizeChange={view.changePageSize}
                            itemsPerPageLabel={t('OFFER_REQUESTS.PAGINATION.ITEMS_PER_PAGE')}
                            paginationAriaLabel={t('OFFER_REQUESTS.PAGINATION.LABEL')}
                        />
                    ) : undefined
                }>
                <OpenOfferRequestsList
                    offerRequests={offerRequests}
                    onSelectOfferRequest={(offerRequest) => {
                        void navigate({
                            to: '/open-requests/$offerRequestLeadUuid',
                            params: { offerRequestLeadUuid: offerRequest.uuid },
                        });
                    }}
                    onStatusChange={(offerRequest, status) => updateStatus.mutate({ uuid: offerRequest.uuid, status })}
                />
            </OfferRequestsPageLayout>

            <FeedbackSnackbar
                open={updateStatus.isError}
                severity="error"
                message={t('OPEN_REQUESTS.STATUS_UPDATE_ERROR')}
                onClose={() => updateStatus.reset()}
            />
        </>
    );
}
